import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useNavigate } from "react-router-dom";
import { Alert, Button, CircularProgress, TextField } from "@mui/material";
import { RoutePaths } from "../../../../enumStrings";
import {
  CmpInvoiceActionTypeEnum,
  CmpInvoiceDetailFieldsFragment,
  CmpInvoiceKindEnum,
  CmpInvoiceDocumentEmailModeEnum,
  CmpManageInvoiceRowFieldsFragment,
  CmpOperationalSourceKindEnum,
  CmpPaymentChoiceTypeEnum,
  useCreateCmpInvoiceMutation,
  useDownloadCmpInvoicePdfMutation,
  useEmailCmpInvoiceDocumentMutation,
  useGetCmpAdvertisingOptionsQuery,
  useGetCmpInvoiceCreateLookupsQuery,
  useGetCmpInvoiceDetailLazyQuery,
} from "../../../../graphql/types";
import { useCmpGlobalSettings } from "../../../../hooks/useCmpSettingsSection";
import useGetChamberFromUserIdInAuth from "../../../../hooks/useGetChamberFromUserIdInAuth";
import { useCmpOrganizationNoun } from "../../../../contexts/CmpOrganizationNounContext";
import { getCmpCreditCardPaymentsReady } from "../../../../utils/cmpCreditCardPayments";
import { usePageHeaderOverride } from "../../../Layout/PageContainer";
import { FormPageShell } from "../../../common/dashboard/FormPageShell";
import {
  createInvoiceButtonSx,
  PreviewInvoiceButton,
  SoldToToggle,
} from "../../../common/dashboard/InvoiceFormControls";
import { hasUnsavedFormChanges } from "../../../common/formStateDirty";
import UnsavedChangesPrompt from "../../../common/UnsavedChangesPrompt";
import ActionSuccessDialog from "../../../common/dashboard/ActionSuccessDialog";
import {
  manageInvoiceRowFromDetail,
  toBackendInvoicePartySnapshot,
  toBackendLineItems,
} from "../invoiceBackendAdapters";
import {
  chamberDateInputToUtcMilli,
  getChamberLocalDateInputValue,
  getInvoiceDueDateInput,
} from "../invoiceDueDate";
import { runCmpInvoiceCreateOperation } from "../invoiceCreateOperation";
import { manageInvoiceActionApolloContext } from "../ManageInvoices/manageInvoiceActionApolloContext";
import {
  getInvoiceEmailSuccessContent,
  createInvoiceEmailFailureError,
  getSavedInvoiceRecipientEmail,
  wasOutboundEmailSent,
} from "../invoiceDeliveryFeedback";
import { downloadBase64File } from "../invoiceFileDownloads";
import { formatCurrency } from "../invoiceFormatting";
import { CMP_INVOICE_CREATE_LOOKUP_LIMIT } from "../invoiceLookupConfig";
import {
  getStaffInvoiceDraftPaymentMethods,
  InvoiceDraftDocumentData,
  InvoicePreviewModal,
  UnsavedInvoicePreviewModal,
} from "../ManageInvoices/ManageInvoiceActionModals";
import { invoiceReadApolloContext } from "../ManageInvoices/usePagedManageInvoices";
import { InvoiceSection } from "../InvoiceMockComponents";
import { InvoiceDesignScope, invoiceFieldSx } from "../InvoiceDesignSystem";
import InvoiceSearchableSelect from "../InvoiceSearchableSelect";
import InvoiceCreatedDialog from "../InvoiceCreatedDialog";
import type { InvoiceCreatedEmailResult } from "../InvoiceCreatedDialog";
import { invoicePageLayoutPresentation } from "../invoicePageHeaderPresentation";
import {
  CreateInvoiceFormDirtyState,
  getCreateInvoiceFormDirtyState,
} from "../invoiceFormDirtyState";
import { runManageInvoiceActionOperation } from "../ManageInvoices/manageInvoiceActionOperation";
import {
  getInvoiceSubtotalInCents,
  getInvoiceTaxInCents,
  getInvoiceTotalInCents,
  InvoicePartySnapshot,
  MockInvoiceLineItem,
  todayIso,
} from "../mockInvoiceData";
import { AdvertisingOptionsManager } from "./AdvertisingOptionsManager";
import { emptyInvoicePartySnapshot } from "../InvoicePartyFields";
import GoogleAddressTextField from "../../../common/GoogleAddressTextField";
import { googleAddressToInvoiceAddress } from "../../../../utils/googleAddressAutocomplete";
import CountrySelectField from "../../../common/dashboard/CountrySelectField";
import CalendarDateInput, {
  isCalendarDateValue,
} from "../../../common/dashboard/CalendarDateInput";
import {
  createEmptyLineItem,
  formatTaxSummaryLabel,
  getCategoryDefaults,
  hasUnavailableCatalogOption,
  getMemberAddressCorrectionVisibility,
  InvoiceTab,
  isPristineEmptyLineItem,
  getInvoiceLineItemErrors,
  isValidInvoiceEmail,
} from "./createInvoiceHelpers";
import {
  InvoiceLineItemsTable,
  LineItemFieldErrors,
} from "./InvoiceLineItemsTable";
import { scrollToFirstInvalidField } from "../../../common/dashboard/scrollToFirstInvalidField";
import { CALLER_HANDLED_USER_ERROR_CONTEXT } from "../../../../utils/apolloErrorPolicy";
import {
  buildStaffErrorFallback,
  getUserFacingErrorText,
  NETWORK_ERROR_TEXT,
  UserFacingError,
} from "../../../../utils/userFacingError";
import {
  EMAIL_FORMAT_MESSAGE,
  FIX_MARKED_FIELDS_MESSAGE,
} from "../../../../utils/fieldMessages";
import { INVOICE_EMAIL_UNCONFIRMED_TEXT } from "../invoiceEmailText";
import { formatStaffPhoneInput } from "../../../../utils/memberContactFormatting";

const shouldMarkCmpInvoiceModuleTestData =
  process.env.NODE_ENV === "development";

// POL-019 (B5): per-field validation errors, Members FormErrors pattern
// (CreateMember.tsx). Line-item errors use `lineItems.{id}.{field}` keys.
type FormErrors = Record<string, string>;


type CreateInvoiceProps = Readonly<{
  invoiceType: InvoiceTab;
}>;

const CreateInvoice = ({ invoiceType }: CreateInvoiceProps) => {
  const navigate = useNavigate();
  const setPageHeaderOverride = usePageHeaderOverride();
  const noun = useCmpOrganizationNoun();
  const chamber = useGetChamberFromUserIdInAuth();
  const chamberId = chamber?.id || "";
  const { data: chamberSettingsData } = useCmpGlobalSettings(
    chamberId,
    "cache-first",
  );

  const activeTab = invoiceType;
  // INV-BLAIR-001: invoices can target an existing member or a one-off,
  // non-member customer. The backend chamberMemberId is optional; in
  // non-member mode we omit it and require an editable customer name instead.
  const [customerKind, setCustomerKind] = useState<"member" | "non-member">(
    "member",
  );
  const [selectedMemberId, setSelectedMemberId] = useState("");
  const [customerName, setCustomerName] = useState("");
  const [contactName, setContactName] = useState("");
  const [contactEmail, setContactEmail] = useState("");
  const [contactPhone, setContactPhone] = useState("");
  const [street, setStreet] = useState("");
  const [addressLine2, setAddressLine2] = useState("");
  const [city, setCity] = useState("");
  const [province, setProvince] = useState("");
  const [postalCode, setPostalCode] = useState("");
  const [country, setCountry] = useState("Canada");
  // New Custom and Advertising invoices use the entered Sold To party as
  // Bill To. Historical snapshots are preserved on saved invoices.
  const payerSameAsSoldTo = true;
  const billToParty = useMemo(emptyInvoicePartySnapshot, []);
  const [issueDate, setIssueDate] = useState(todayIso());
  const [dueDate, setDueDate] = useState(todayIso());
  const [dueDateManuallyEdited, setDueDateManuallyEdited] = useState(false);
  const [referenceNumber, setReferenceNumber] = useState("");
  const [notes, setNotes] = useState("");
  // Claude Design ActionSuccessModal after a draft is saved from the preview.
  const [draftSavedMessage, setDraftSavedMessage] = useState<string | null>(
    null,
  );
  const [lineItems, setLineItems] = useState<MockInvoiceLineItem[]>([
    createEmptyLineItem({}),
  ]);
  const [createdInvoice, setCreatedInvoice] = useState<{
    id: string;
    invoiceNumber: string;
    recipientEmail: string | null;
  } | null>(null);
  const [createdInvoiceActionError, setCreatedInvoiceActionError] = useState<
    string | null
  >(null);
  // Claude Design: the created pop-up "Print Invoice" opens the saved preview.
  const [createdInvoicePreview, setCreatedInvoicePreview] = useState<{
    detail: CmpInvoiceDetailFieldsFragment;
    row: CmpManageInvoiceRowFieldsFragment;
  } | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [formErrors, setFormErrors] = useState<FormErrors>({});
  const [showEmailPreview, setShowEmailPreview] = useState(false);
  const seededInitialLineDefaults = useRef(false);
  const [formBaseline, setFormBaseline] = useState<CreateInvoiceFormDirtyState>(
    () =>
      getCreateInvoiceFormDirtyState({
        billToParty,
        customerKind,
        dueDate,
        issueDate,
        lineItems,
        notes,
        payerSameAsSoldTo,
        referenceNumber,
        selectedMemberId,
        soldToParty: {
          customerName,
          contactName,
          contactEmail,
          contactPhone,
          addressLine1: street,
          addressLine2,
          city,
          province,
          postalCode,
          country,
        },
      }),
  );

  const {
    data: lookupData,
    loading: lookupsLoading,
    error: lookupsError,
  } = useGetCmpInvoiceCreateLookupsQuery({
    variables: {
      // This page does not show the event registration or sponsorship
      // options, so the backend skips those reads.
      input: {
        chamberId,
        limit: CMP_INVOICE_CREATE_LOOKUP_LIMIT,
        includeEventOptions: false,
      },
    },
    skip: !chamberId,
    fetchPolicy: "cache-and-network",
    // Lookup errors show on this page, not the full-page error.
    context: CALLER_HANDLED_USER_ERROR_CONTEXT,
  });
  const {
    data: advertisingData,
    loading: advertisingLoading,
    refetch: refetchAdvertisingOptions,
  } = useGetCmpAdvertisingOptionsQuery({
    variables: { input: { chamberId } },
    skip: !chamberId,
    fetchPolicy: "cache-and-network",
    context: CALLER_HANDLED_USER_ERROR_CONTEXT,
  });
  // UX audit INV-3: a rejected create stays in the preview. Without the
  // caller context the global Apollo error link sends staff to /error500.
  const [createCmpInvoice, { loading: creatingInvoice }] =
    useCreateCmpInvoiceMutation({ context: manageInvoiceActionApolloContext });
  const [downloadCmpInvoicePdf, { loading: downloadingPdf }] =
    useDownloadCmpInvoicePdfMutation({ context: manageInvoiceActionApolloContext });
  const [emailCmpInvoiceDocument, { loading: emailingInvoiceDocument }] =
    useEmailCmpInvoiceDocumentMutation({ context: manageInvoiceActionApolloContext });
  const [getCreatedInvoiceDetail, { loading: loadingCreatedInvoiceDetail }] =
    useGetCmpInvoiceDetailLazyQuery({
      context: invoiceReadApolloContext,
      fetchPolicy: "network-only",
    });

  const members = useMemo(
    () => lookupData?.getCmpInvoiceCreateLookups.members || [],
    [lookupData],
  );
  const invoiceDefaults =
    lookupData?.getCmpInvoiceCreateLookups.invoiceDefaults || null;
  const invoicePaymentTerms =
    chamberSettingsData?.getChamberMemberProSettings.paymentsAndTaxes
      .invoicePaymentTerms;
  const chamberTimeZone =
    chamberSettingsData?.getChamberMemberProSettings.setup.timeZone;
  const stripeConnect =
    chamberSettingsData?.getChamberMemberProSettings.paymentsAndTaxes
      .stripeConnect;
  const configuredManualPaymentMethods = useMemo(
    () =>
      chamberSettingsData?.getChamberMemberProSettings.paymentsAndTaxes
        .manualPaymentMethods || [],
    [chamberSettingsData],
  );
  const creditCardPaymentsReady = getCmpCreditCardPaymentsReady(stripeConnect);
  const advertisingOptions = useMemo(
    () => advertisingData?.getCmpAdvertisingOptions.advertisingOptions || [],
    [advertisingData],
  );
  const customLineItemSuggestions = useMemo(
    () =>
      (
        lookupData?.getCmpInvoiceCreateLookups.customLineItemSuggestions || []
      ).map((suggestion, index) => ({
        code: suggestion.itemCode || "",
        id: `custom-suggestion-${index}-${
          suggestion.itemCode || suggestion.description
        }`,
        name: suggestion.description,
        priceInCents: suggestion.unitPriceInCents,
        // Suggestions are historical descriptions and prices. Current
        // invoices always keep the active Chamber Setup tax rate.
        taxRatePercentage: undefined,
      })),
    [lookupData],
  );
  const lineItemCatalogOptions = useMemo(
    () =>
      activeTab === "advertising"
        ? advertisingOptions
        : customLineItemSuggestions,
    [activeTab, advertisingOptions, customLineItemSuggestions],
  );

  const categoryDefaults = getCategoryDefaults(invoiceDefaults, activeTab);
  const accountNumber = categoryDefaults?.accountNumber || "";
  const defaultTaxRate = categoryDefaults?.taxEnabled
    ? categoryDefaults.taxRatePercentage || 0
    : 0;

  const subtotalInCents = getInvoiceSubtotalInCents({ lineItems });
  const taxInCents = getInvoiceTaxInCents({ lineItems });
  const totalInCents = getInvoiceTotalInCents({ lineItems });
  const submitting = creatingInvoice || downloadingPdf;

  // Invoice Date starts as today in the chamber time zone (Moeen, Sept 24
  // 2026). The browser date is only a placeholder until settings load, and a
  // date the user already changed is kept.
  const chamberIssueDateSeeded = useRef(false);
  useEffect(() => {
    if (!chamberTimeZone || chamberIssueDateSeeded.current) {
      return;
    }
    chamberIssueDateSeeded.current = true;
    const chamberToday = getChamberLocalDateInputValue({
      timeZone: chamberTimeZone,
    });
    if (!chamberToday || issueDate !== formBaseline.issueDate) {
      return;
    }
    setIssueDate(chamberToday);
    setFormBaseline((current) => ({ ...current, issueDate: chamberToday }));
  }, [chamberTimeZone, formBaseline.issueDate, issueDate]);

  useEffect(() => {
    if (!invoicePaymentTerms || dueDateManuallyEdited) {
      return;
    }

    const nextDueDate = getInvoiceDueDateInput({
      invoicePaymentTerms,
      issueDate,
      timeZone: chamberTimeZone,
    });
    const nextBaselineDueDate = getInvoiceDueDateInput({
      invoicePaymentTerms,
      issueDate: formBaseline.issueDate,
      timeZone: chamberTimeZone,
    });

    if (nextDueDate !== dueDate) {
      setDueDate(nextDueDate);
    }
    if (nextBaselineDueDate !== formBaseline.dueDate) {
      setFormBaseline((current) => ({
        ...current,
        dueDate: nextBaselineDueDate,
      }));
    }
  }, [
    chamberTimeZone,
    dueDate,
    dueDateManuallyEdited,
    formBaseline.dueDate,
    formBaseline.issueDate,
    invoicePaymentTerms,
    issueDate,
  ]);

  useEffect(() => {
    if (!categoryDefaults || seededInitialLineDefaults.current) {
      return;
    }

    seededInitialLineDefaults.current = true;
    if (lineItems.length !== 1 || !isPristineEmptyLineItem(lineItems[0])) {
      return;
    }

    const nextLineItems = [
      {
        ...lineItems[0],
        accountNumber,
        taxRate: defaultTaxRate,
      },
    ];
    const lineItemsStillAtBaseline = !hasUnsavedFormChanges({
      baseline: formBaseline.lineItems,
      current: lineItems,
    });

    setLineItems(nextLineItems);
    if (lineItemsStillAtBaseline) {
      setFormBaseline((current) => ({
        ...current,
        lineItems: nextLineItems.map((lineItem) => ({ ...lineItem })),
      }));
    }
  }, [
    accountNumber,
    categoryDefaults,
    defaultTaxRate,
    formBaseline.lineItems,
    lineItems,
  ]);

  const getFieldError = (key: string) => formErrors[key] || "";
  const memberAddressCorrection = getMemberAddressCorrectionVisibility({
    country,
    countryError: getFieldError("country"),
    customerKind,
    province,
    provinceError: getFieldError("province"),
    selectedMemberId,
  });
  const soldToParty = useMemo<InvoicePartySnapshot>(
    () => ({
      customerName,
      contactName,
      contactEmail,
      contactPhone,
      addressLine1: street,
      addressLine2,
      city,
      province,
      postalCode,
      country,
    }),
    [
      addressLine2,
      city,
      contactEmail,
      contactName,
      contactPhone,
      country,
      customerName,
      postalCode,
      province,
      street,
    ],
  );
  const actualPayer = soldToParty;
  const currentFormState = useMemo(
    () =>
      getCreateInvoiceFormDirtyState({
        billToParty,
        customerKind,
        dueDate,
        issueDate,
        lineItems,
        notes,
        payerSameAsSoldTo,
        referenceNumber,
        selectedMemberId,
        soldToParty,
      }),
    [
      billToParty,
      customerKind,
      dueDate,
      issueDate,
      lineItems,
      notes,
      payerSameAsSoldTo,
      referenceNumber,
      selectedMemberId,
      soldToParty,
    ],
  );
  const isDirty = hasUnsavedFormChanges({
    baseline: formBaseline,
    current: currentFormState,
  });

  // POL-019 (B5): a field's error clears as soon as the field changes.
  const clearFieldError = (...keys: string[]) =>
    setFormErrors((previous) => {
      const staleKeys = keys.filter((key) => previous[key]);

      if (staleKeys.length === 0) {
        return previous;
      }

      const next = { ...previous };
      staleKeys.forEach((key) => delete next[key]);
      return next;
    });

  const handleSelectMember = (memberId: string) => {
    setSelectedMemberId(memberId);
    // Selecting a member fills the billing snapshot fields below, so their
    // stale required-field errors clear along with the member error.
    clearFieldError(
      "memberId",
      "customerName",
      "street",
      "city",
      "province",
      "postalCode",
      "country",
      "contactName",
      "contactEmail",
      "contactPhone",
    );

    const option = members.find((candidate) => candidate.id === memberId);
    if (!option) {
      return;
    }

    setCustomerName(option.billingSnapshot.customerName);
    setContactName(option.billingSnapshot.contactName || "");
    setContactEmail(option.billingSnapshot.contactEmail);
    setContactPhone(option.billingSnapshot.contactPhone || "");
    setStreet(option.billingSnapshot.addressLine1 || "");
    setAddressLine2(option.billingSnapshot.addressLine2 || "");
    setCity(option.billingSnapshot.city || "");
    setProvince(option.billingSnapshot.province || "");
    setPostalCode(option.billingSnapshot.postalCode || "");
    setCountry(option.billingSnapshot.country || "");
  };

  // INV-BLAIR-001: switching to a non-member customer clears the member
  // selection (so we never send a stale chamberMemberId) but keeps the
  // already-entered billing fields, which stay editable in both modes.
  const handleChangeCustomerKind = (kind: "member" | "non-member") => {
    if (kind === customerKind) {
      return;
    }

    setCustomerKind(kind);
    if (kind === "non-member") {
      setSelectedMemberId("");
    }
  };

  const handleLineItemsChange = (nextLineItems: MockInvoiceLineItem[]) => {
    // POL-019 (B5): clear line errors for removed lines and for line fields
    // that changed. The unit-price constraint is collective (at least one
    // line > $0.00), so any price edit clears all unit-price errors until the
    // next submit re-evaluates.
    setFormErrors((previous) => {
      const lineKeys = Object.keys(previous).filter(
        (key) => key === "lineItems" || key.startsWith("lineItems."),
      );

      if (lineKeys.length === 0) {
        return previous;
      }

      const previousById = new Map(lineItems.map((item) => [item.id, item]));
      const nextById = new Map(nextLineItems.map((item) => [item.id, item]));
      const anyPriceChanged = nextLineItems.some((item) => {
        const previousItem = previousById.get(item.id);
        return previousItem
          ? previousItem.unitPriceInCents !== item.unitPriceInCents
          : false;
      });
      const next = { ...previous };
      let changed = false;

      if (next.lineItems) {
        delete next.lineItems;
        changed = true;
      }

      lineKeys.forEach((key) => {
        const match = key.match(
          /^lineItems\.(.+)\.(code|description|quantity|unitPrice)$/,
        );

        if (!match) {
          return;
        }

        const [, lineItemId, field] = match;
        const nextItem = nextById.get(lineItemId);

        if (!nextItem) {
          delete next[key];
          changed = true;
          return;
        }

        if (field === "unitPrice" && anyPriceChanged) {
          delete next[key];
          changed = true;
          return;
        }

        const previousItem = previousById.get(lineItemId);
        if (
          previousItem &&
          ((field === "code" && previousItem.code !== nextItem.code) ||
            (field === "quantity" &&
              previousItem.quantity !== nextItem.quantity))
        ) {
          delete next[key];
          changed = true;
          return;
        }
        if (
          field === "description" &&
          previousItem &&
          (previousItem.description !== nextItem.description ||
            previousItem.catalogOptionId !== nextItem.catalogOptionId)
        ) {
          delete next[key];
          changed = true;
        }
      });

      return changed ? next : previous;
    });
    setLineItems(nextLineItems);
  };

  // Claude Design required-field messages (red asterisks). A plain saved
  // draft may be incomplete. Previewing, creating, approving, printing, or
  // sending crosses an invoice-document boundary and requires every field.
  const getInvoiceFormErrors = useCallback(
    ({ requireCompleteInvoice }: { requireCompleteInvoice: boolean }) => {
      const isMemberInvoice = customerKind === "member";
      const selectedMember = members.find(
        (member) => member.id === selectedMemberId,
      );
      const nextErrors: FormErrors = {};

      if (isMemberInvoice && !selectedMember) {
        nextErrors.memberId = "Member is required";
      }

      if (!isMemberInvoice && !customerName.trim()) {
        nextErrors.customerName = "Customer / Company Name is required";
      }

      if (!contactEmail.trim()) {
        nextErrors.contactEmail = "Email is required";
      } else if (!isValidInvoiceEmail(contactEmail)) {
        nextErrors.contactEmail = EMAIL_FORMAT_MESSAGE;
      }

      if (requireCompleteInvoice) {
        // New invoices bill the Sold To party (payerSameAsSoldTo).
        const payer = actualPayer;

        if (!payer.customerName.trim()) {
          nextErrors.customerName = "Customer / Company Name is required";
        }
        // The billing address is optional (approved Oct 12 2026). The
        // backend issues the invoice and leaves missing fields off it.
        if (!payer.contactName.trim()) {
          nextErrors.contactName = "Billing Contact Name is required";
        }
        if (!payer.contactPhone.trim()) {
          nextErrors.contactPhone = "Phone is required";
        }
        if (!issueDate) {
          nextErrors.issueDate = "Invoice Date is required";
        } else if (!isCalendarDateValue(issueDate)) {
          nextErrors.issueDate = "Enter a valid Invoice Date.";
        }
        if (!dueDate) {
          nextErrors.dueDate = "Due Date is required";
        } else if (!isCalendarDateValue(dueDate)) {
          nextErrors.dueDate = "Enter a valid Due Date.";
        }
        if (lineItems.length === 0) {
          nextErrors.lineItems = "Add at least one line item.";
        }

        Object.assign(nextErrors, getInvoiceLineItemErrors(lineItems));
      }

      if (activeTab === "advertising") {
        lineItems.forEach((item) => {
          if (
            hasUnavailableCatalogOption({
              lineItem: item,
              options: advertisingOptions,
            })
          ) {
            nextErrors[`lineItems.${item.id}.description`] =
              "This advertising option is no longer in the list. Select another advertising option for this line.";
          }
        });
      }

      if (totalInCents <= 0) {
        if (lineItems.length === 0) {
          nextErrors.lineItems = "Add at least one line item.";
        }
        lineItems.forEach((item) => {
          if (item.unitPriceInCents <= 0) {
            nextErrors[`lineItems.${item.id}.unitPrice`] =
              "Enter a unit price greater than $0.00.";
          }
        });
      }

      return nextErrors;
    },
    [
      activeTab,
      actualPayer,
      advertisingOptions,
      contactEmail,
      customerKind,
      customerName,
      dueDate,
      issueDate,
      lineItems,
      members,
      selectedMemberId,
      totalInCents,
    ],
  );

  const submitInvoice = useCallback(
    async ({
      createAsDraft,
      navigateAfterSave = true,
      showDraftSavedMessage = false,
    }: {
      createAsDraft: boolean;
      navigateAfterSave?: boolean;
      showDraftSavedMessage?: boolean;
    }) => {
      const isMemberInvoice = customerKind === "member";
      const selectedMember = members.find(
        (member) => member.id === selectedMemberId,
      );
      const nextErrors = getInvoiceFormErrors({
        requireCompleteInvoice: !createAsDraft,
      });

      setFormErrors(nextErrors);

      if (Object.keys(nextErrors).length > 0) {
        setErrorMessage(null);
        scrollToFirstInvalidField();
        return false;
      }

      try {
        setErrorMessage(null);

        if (!chamberId) {
          throw new UserFacingError(`${noun.Singular} is still loading. Try again in a moment.`);
        }

        const createInvoiceInput = {
          chamberId,
          sourceKind:
            activeTab === "advertising"
              ? CmpOperationalSourceKindEnum.Advertising
              : CmpOperationalSourceKindEnum.CustomInvoice,
          chamberMemberId: isMemberInvoice
            ? selectedMember?.chamberMemberId || undefined
            : undefined,
          billingSnapshot: toBackendInvoicePartySnapshot(soldToParty),
          billToSnapshot: payerSameAsSoldTo
            ? undefined
            : toBackendInvoicePartySnapshot(billToParty),
          lineItems: toBackendLineItems(lineItems),
          paymentChoice: {
            type: creditCardPaymentsReady
              ? CmpPaymentChoiceTypeEnum.CreditCard
              : CmpPaymentChoiceTypeEnum.Manual,
            autoRenew: false,
            emailCommunicationConsentConfirmed: true,
          },
          issuedAtUtcMilli: chamberDateInputToUtcMilli({
            dateInput: issueDate,
            timeZone: chamberTimeZone,
          }),
          dueAtUtcMilli: chamberDateInputToUtcMilli({
            dateInput: dueDate,
            timeZone: chamberTimeZone,
          }),
          internalNotes: notes.trim() || undefined,
          referencePurchaseOrder: referenceNumber.trim() || undefined,
          createAsDraft,
          isCmpInvoiceModuleTestData:
            shouldMarkCmpInvoiceModuleTestData || undefined,
        };

        const result = await runCmpInvoiceCreateOperation({
          execute: (input) =>
            createCmpInvoice({
              variables: { input },
            }),
          input: createInvoiceInput,
          isAuthoritativelySaved: (mutationResult) =>
            !!mutationResult.data?.createCmpInvoice.invoiceSavedSuccessfully &&
            !!mutationResult.data.createCmpInvoice.invoice?.id,
        });

        const created = result.data?.createCmpInvoice;
        const invoiceId = created?.invoice?.id;

        if (!created?.invoiceSavedSuccessfully || !invoiceId) {
          throw new Error("Invoice could not be saved.");
        }

        // Only an authoritative save moves the baseline. Validation and
        // mutation failures keep the current unsaved baseline in place.
        setFormBaseline(currentFormState);
        if (!createAsDraft) {
          setShowEmailPreview(false);
          setCreatedInvoice({
            id: invoiceId,
            invoiceNumber: created.invoice?.invoiceNumber || "Invoice",
            recipientEmail: getSavedInvoiceRecipientEmail(
              created.invoice || {},
            ),
          });
        } else if (showDraftSavedMessage) {
          const draftCustomerName =
            actualPayer.customerName.trim() || "the member";

          setDraftSavedMessage(
            `The draft invoice for ${draftCustomerName} has been saved. You can return to it at any time to review and create the invoice.`,
          );
        }
        if (navigateAfterSave) {
          navigate(RoutePaths.chamberAdmin_invoicing_manage);
        }
        return true;
      } catch (error) {
        setErrorMessage(
          getUserFacingErrorText(
            error,
            buildStaffErrorFallback(
              createAsDraft ? "save the draft invoice" : "create the invoice",
            ),
          ),
        );
        return false;
      }
    },
    [
      activeTab,
      actualPayer,
      billToParty,
      chamberId,
      chamberTimeZone,
      createCmpInvoice,
      creditCardPaymentsReady,
      currentFormState,
      customerKind,
      dueDate,
      getInvoiceFormErrors,
      issueDate,
      lineItems,
      members,
      navigate,
      notes,
      noun,
      payerSameAsSoldTo,
      referenceNumber,
      selectedMemberId,
      soldToParty,
    ],
  );

  const pageTitle =
    activeTab === "advertising"
      ? "Create Advertising Invoice"
      : "Create Custom Invoice";

  useEffect(() => {
    setPageHeaderOverride({
      ...invoicePageLayoutPresentation,
      // Claude Design subtitle (spacing before the comma corrected).
      description:
        "Create invoice, save as draft, preview invoice, apply payment, print and email.",
      title: pageTitle,
      withHeaderDivider: true,
    });

    return () => setPageHeaderOverride(null);
  }, [pageTitle, setPageHeaderOverride]);

  // TICK-10 (B5): the totals row and the email preview share the tax
  // dropdown's "Tax (rate%)" label shape (guarded against double-suffixing).
  const taxSummaryLabel = formatTaxSummaryLabel(
    categoryDefaults?.taxRateLabel,
    categoryDefaults?.taxRatePercentage,
  );
  // A member with a separate Billing/Mailing Address: Sold To prints the
  // company details (the saved invoice and its PDF do the same).
  const memberCompanySoldTo =
    customerKind === "member"
      ? members.find((member) => member.id === selectedMemberId)?.soldToSnapshot
      : null;
  const draftDocument = useMemo<InvoiceDraftDocumentData>(
    () => ({
      billingSnapshot: soldToParty,
      billToSnapshot: payerSameAsSoldTo ? soldToParty : billToParty,
      soldToSnapshot: memberCompanySoldTo
        ? { ...memberCompanySoldTo, customerName: soldToParty.customerName }
        : null,
      customerName: actualPayer.customerName,
      dueDate,
      invoiceDate: issueDate,
      invoiceKind:
        activeTab === "advertising"
          ? CmpInvoiceKindEnum.Advertising
          : CmpInvoiceKindEnum.Custom,
      lineItems: lineItems
        .filter(
          (item) =>
            item.description.trim() ||
            item.quantity > 0 ||
            item.unitPriceInCents > 0,
        )
        .map((item) => ({
          code: item.code || null,
          description: item.description.trim() || item.code || "Invoice item",
          id: item.id,
          quantity: item.quantity,
          totalInCents: item.quantity * item.unitPriceInCents,
          unitPriceInCents: item.unitPriceInCents,
        })),
      manualPaymentMethods: getStaffInvoiceDraftPaymentMethods({
        creditCardPaymentsReady,
        manualPaymentMethods: configuredManualPaymentMethods,
      }),
      referencePurchaseOrder: referenceNumber.trim() || null,
      subtotalInCents,
      taxInCents,
      taxLabel: taxSummaryLabel,
      taxRegistrationNumber: invoiceDefaults?.taxRegistrationNumber,
      totalInCents,
    }),
    [
      activeTab,
      actualPayer.customerName,
      billToParty,
      memberCompanySoldTo,
      configuredManualPaymentMethods,
      creditCardPaymentsReady,
      dueDate,
      invoiceDefaults?.taxRegistrationNumber,
      issueDate,
      lineItems,
      payerSameAsSoldTo,
      referenceNumber,
      soldToParty,
      subtotalInCents,
      taxInCents,
      taxSummaryLabel,
      totalInCents,
    ],
  );

  // Claude Design "Preview Invoice": validate the required fields first.
  const handleOpenPreview = () => {
    const nextErrors = getInvoiceFormErrors({ requireCompleteInvoice: true });

    setFormErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) {
      setErrorMessage(null);
      scrollToFirstInvalidField();
      return;
    }

    setErrorMessage(null);
    setShowEmailPreview(true);
  };

  const handleDownloadCreatedInvoicePdf = async () => {
    if (!createdInvoice) {
      return;
    }

    try {
      setCreatedInvoiceActionError(null);
      const result = await downloadCmpInvoicePdf({
        variables: {
          input: {
            chamberId,
            invoiceId: createdInvoice.id,
            isCmpInvoiceModuleTestData:
              shouldMarkCmpInvoiceModuleTestData || undefined,
          },
        },
      });
      const download = result.data?.downloadCmpInvoicePdf.download;

      if (!download) {
        throw new Error("Invoice PDF download did not return a file.");
      }

      downloadBase64File(download);
    } catch (error) {
      setCreatedInvoicePreview(null);
      setCreatedInvoiceActionError(
        getUserFacingErrorText(
          error,
          buildStaffErrorFallback(
            `download the PDF for invoice ${createdInvoice.invoiceNumber}`,
          ),
        ),
      );
    }
  };

  // POL-019 (B5): line-item errors regrouped by line id for the table.
  const lineItemErrors: Record<string, LineItemFieldErrors> = {};
  Object.entries(formErrors).forEach(([key, message]) => {
    const match = key.match(
      /^lineItems\.(.+)\.(code|description|quantity|unitPrice)$/,
    );

    if (!match) {
      return;
    }

    const [, lineItemId, field] = match;
    lineItemErrors[lineItemId] = {
      ...lineItemErrors[lineItemId],
      [field]: message,
    };
  });
  const firstFormError = Object.values(formErrors)[0];

  return (
    <InvoiceDesignScope>
      <FormPageShell
        headingId="create-invoice-page"
        heading={pageTitle}
        mainClassName="w-full"
        mainPaddingTopClassName="pt-[14px]"
        contentClassName=""
        beforeMain={
          <>
            <UnsavedChangesPrompt
              variant="page"
              when={isDirty}
              saving={creatingInvoice}
            />

            <UnsavedInvoicePreviewModal
              actionLoading={creatingInvoice || downloadingPdf}
              chamber={chamber}
              draftDocument={draftDocument}
              errorMessage={errorMessage}
              onClose={() => setShowEmailPreview(false)}
              onCreateInvoice={() => {
                void submitInvoice({
                  createAsDraft: false,
                  navigateAfterSave: false,
                });
              }}
              onSaveDraft={async () => {
                // Save errors show on the page, so close the preview first.
                setShowEmailPreview(false);
                await submitInvoice({
                  createAsDraft: true,
                  navigateAfterSave: false,
                  showDraftSavedMessage: true,
                });
              }}
              open={showEmailPreview}
            />

            <ActionSuccessDialog
              highlights={[actualPayer.customerName.trim() || "the member"]}
              message={draftSavedMessage}
              onClose={() => {
                setDraftSavedMessage(null);
                navigate(RoutePaths.chamberAdmin_invoicing_manage);
              }}
            />
          </>
        }
        alerts={
          <>
            {lookupsError ? (
              <Alert severity="error" className="mb-5">
                {getUserFacingErrorText(
                  lookupsError,
                  buildStaffErrorFallback("load the member list for this invoice"),
                )}
              </Alert>
            ) : null}
            {errorMessage ? (
              <Alert
                severity="error"
                className="mb-5"
                onClose={() => setErrorMessage(null)}
              >
                {errorMessage}
              </Alert>
            ) : null}
          </>
        }
      >
        <div className="space-y-[22px] rounded-xl border border-[#ECECEC] bg-white p-4 shadow-[0_1px_2px_rgba(0,0,0,0.04)] sm:p-7">
          {activeTab === "advertising" ? (
            <AdvertisingOptionsManager
              advertisingOptions={advertisingOptions}
              chamberId={chamberId}
              isCmpInvoiceModuleTestData={shouldMarkCmpInvoiceModuleTestData}
              loading={advertisingLoading}
              onChanged={() => void refetchAdvertisingOptions()}
            />
          ) : null}

          <InvoiceSection
            label="Member"
            title="Member Information"
            variant="plain"
          >
            <div className="space-y-[22px]">
              <div className="flex flex-col gap-1">
                <label className="block text-sm font-bold leading-[normal] text-[#0A0A0A]">
                  Sold To <span className="text-[#E24C4C]">*</span>
                </label>
                <p className="text-xs leading-[normal] text-[#0A0A0A]">
                  Select whether this invoice is for an existing member or a
                  non-member.
                </p>
                <div className="mt-1">
                  <SoldToToggle
                    value={customerKind}
                    onChange={handleChangeCustomerKind}
                  />
                </div>
              </div>

              {customerKind === "member" ? (
                <div
                  data-invoice-field-error={
                    getFieldError("memberId") ? "true" : undefined
                  }
                >
                  <label
                    className={`mb-1 block text-sm font-bold leading-[normal] ${
                      getFieldError("memberId")
                        ? "text-red-600"
                        : "text-[#0A0A0A]"
                    }`}
                  >
                    Member <span className="text-[#E24C4C]">*</span>
                  </label>
                  <p className="mb-2 text-xs leading-[normal] text-[#0A0A0A]">
                    Search or select the member from the drop down.
                  </p>
                  <InvoiceSearchableSelect
                    id={`create-${activeTab}-member`}
                    options={members}
                    getOptionKey={(option) => option.id}
                    // Design: the member list shows the company name only.
                    getOptionLabel={(option) => option.label}
                    value={
                      members.find(
                        (member) => member.id === selectedMemberId,
                      ) || null
                    }
                    disabled={lookupsLoading && members.length === 0}
                    loading={lookupsLoading && members.length === 0}
                    loadingText="Loading members..."
                    noOptionsText="No members found"
                    onChange={(option) =>
                      handleSelectMember(option?.id || "")
                    }
                    placeholder="Select Member Name"
                    searchPlaceholder="Search Member"
                    error={Boolean(getFieldError("memberId"))}
                    helperText={getFieldError("memberId") || undefined}
                    sx={{ maxWidth: 600 }}
                  />
                </div>
              ) : (
                <div
                  data-invoice-field-error={
                    getFieldError("customerName") ? "true" : undefined
                  }
                >
                  <label
                    className={`mb-1 block text-sm font-bold leading-[normal] ${
                      getFieldError("customerName")
                        ? "text-red-600"
                        : "text-[#0A0A0A]"
                    }`}
                  >
                    Customer / Company Name{" "}
                    <span className="text-[#E24C4C]">*</span>
                  </label>
                  <p className="mb-2 text-xs leading-[normal] text-[#0A0A0A]">
                    Enter the customer or company being invoiced.
                  </p>
                  <TextField
                    placeholder="Enter Customer / Company Name"
                    value={customerName}
                    sx={[
                      invoiceFieldSx,
                      {
                        maxWidth: 600,
                        "& .MuiInputBase-root": { minHeight: "42.5px" },
                        "& .MuiInputBase-input": {
                          color: "#0A0A0A",
                          letterSpacing: "normal",
                          padding: "11px 14px",
                          lineHeight: "normal",
                          "&::placeholder": {
                            color: "#9E9E9E",
                            opacity: 1,
                          },
                        },
                        "& .MuiOutlinedInput-notchedOutline": {
                          borderColor: "#ECECEC",
                        },
                        "& .MuiOutlinedInput-root.Mui-error .MuiOutlinedInput-notchedOutline": {
                          borderColor: "#E24C4C",
                        },
                      },
                    ]}
                    error={Boolean(getFieldError("customerName"))}
                    helperText={getFieldError("customerName") || undefined}
                    onChange={({ target }) => {
                      setCustomerName(target.value);
                      clearFieldError("customerName");
                    }}
                    fullWidth
                  />
                </div>
              )}

              <div
                data-invoice-field-error={
                  getFieldError("street") ||
                  getFieldError("city") ||
                  getFieldError("province") ||
                  getFieldError("postalCode") ||
                  getFieldError("country")
                    ? "true"
                    : undefined
                }
              >
                <label
                  className={`mb-1 block text-sm font-bold ${
                    getFieldError("street") ||
                    getFieldError("city") ||
                    getFieldError("province") ||
                    getFieldError("postalCode") ||
                    getFieldError("country")
                      ? "text-red-600"
                      : "text-gray-900"
                  }`}
                >
                  Billing/Mailing Address
                </label>
                <p className="mb-2 text-xs text-black">
                  Edit if not shown correctly
                </p>
                <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                  <GoogleAddressTextField
                    id="create-invoice-street"
                    placeholder="Enter Street Number and Name"
                    value={street}
                    sx={invoiceFieldSx}
                    error={Boolean(getFieldError("street"))}
                    helperText={getFieldError("street") || undefined}
                    onValueChange={(value) => {
                      setStreet(value);
                      clearFieldError("street");
                    }}
                    onAddressSelect={(address) => {
                      // Same mapping as Update Invoice (InvoicePartyFields).
                      const selected = googleAddressToInvoiceAddress(address);
                      setStreet(selected.addressLine1);
                      setAddressLine2(selected.addressLine2);
                      setCity(selected.city);
                      setProvince(selected.province);
                      setPostalCode(selected.postalCode);
                      setCountry(selected.country);
                      clearFieldError("street", "city", "province", "postalCode", "country");
                    }}
                    fullWidth
                  />
                  <TextField
                    placeholder="Enter City Name"
                    value={city}
                    sx={invoiceFieldSx}
                    error={Boolean(getFieldError("city"))}
                    helperText={getFieldError("city") || undefined}
                    onChange={({ target }) => {
                      setCity(target.value);
                      clearFieldError("city");
                    }}
                    fullWidth
                  />
                  <TextField
                    placeholder="Enter Postal Code"
                    value={postalCode}
                    sx={invoiceFieldSx}
                    error={Boolean(getFieldError("postalCode"))}
                    helperText={getFieldError("postalCode") || undefined}
                    onChange={({ target }) => {
                      setPostalCode(target.value);
                      clearFieldError("postalCode");
                    }}
                    fullWidth
                  />
                </div>
                {customerKind === "non-member" ||
                memberAddressCorrection.showProvince ||
                memberAddressCorrection.showCountry ? (
                  <div
                    className={`mt-4 grid grid-cols-1 gap-4 ${
                      customerKind === "member"
                        ? "md:max-w-[600px] md:grid-cols-2"
                        : "md:grid-cols-3"
                    }`}
                  >
                    {customerKind === "non-member" ? (
                      <TextField
                        placeholder="Address Line 2 (Optional)"
                        value={addressLine2}
                        sx={invoiceFieldSx}
                        onChange={({ target }) => setAddressLine2(target.value)}
                        fullWidth
                      />
                    ) : null}
                    {customerKind === "non-member" ||
                    memberAddressCorrection.showProvince ? (
                      <TextField
                        placeholder="Enter Province / Territory"
                        value={province}
                        sx={invoiceFieldSx}
                        error={Boolean(getFieldError("province"))}
                        helperText={getFieldError("province") || undefined}
                        onChange={({ target }) => {
                          setProvince(target.value);
                          clearFieldError("province");
                        }}
                        fullWidth
                      />
                    ) : null}
                    {customerKind === "non-member" ||
                    memberAddressCorrection.showCountry ? (
                      <CountrySelectField
                        id="create-invoice-country"
                        value={country}
                        sx={invoiceFieldSx}
                        error={Boolean(getFieldError("country"))}
                        helperText={getFieldError("country") || undefined}
                        onValueChange={(value) => {
                          setCountry(value);
                          clearFieldError("country");
                        }}
                        fullWidth
                      />
                    ) : null}
                  </div>
                ) : null}
              </div>

              <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                <div
                  data-invoice-field-error={
                    getFieldError("contactName") ? "true" : undefined
                  }
                >
                  <label
                    className={`mb-1 block text-sm font-bold ${
                      getFieldError("contactName")
                        ? "text-red-600"
                        : "text-gray-900"
                    }`}
                  >
                    Billing Contact Name <span className="text-red-500">*</span>
                  </label>
                  <TextField
                    placeholder="Enter Contact Name"
                    value={contactName}
                    sx={invoiceFieldSx}
                    error={Boolean(getFieldError("contactName"))}
                    helperText={getFieldError("contactName") || undefined}
                    onChange={({ target }) => {
                      setContactName(target.value);
                      clearFieldError("contactName");
                    }}
                    fullWidth
                  />
                </div>
                <div
                  data-invoice-field-error={
                    getFieldError("contactEmail") ? "true" : undefined
                  }
                >
                  <label
                    className={`mb-1 block text-sm font-bold ${
                      getFieldError("contactEmail")
                        ? "text-red-600"
                        : "text-gray-900"
                    }`}
                  >
                    Email <span className="text-red-500">*</span>
                  </label>
                  <TextField
                    placeholder="Enter Contact Email"
                    value={contactEmail}
                    sx={invoiceFieldSx}
                    error={Boolean(getFieldError("contactEmail"))}
                    helperText={getFieldError("contactEmail") || undefined}
                    onChange={({ target }) => {
                      setContactEmail(target.value);
                      clearFieldError("contactEmail");
                    }}
                    fullWidth
                  />
                </div>
                <div
                  data-invoice-field-error={
                    getFieldError("contactPhone") ? "true" : undefined
                  }
                >
                  {/* Claude Design marks Phone required (red asterisk), so
                    creating or previewing the invoice enforces it. */}
                  <label
                    className={`mb-1 block text-sm font-bold ${
                      getFieldError("contactPhone")
                        ? "text-red-600"
                        : "text-gray-900"
                    }`}
                  >
                    Phone <span className="text-red-500">*</span>
                  </label>
                  <TextField
                    placeholder="Enter Phone Number"
                    value={contactPhone}
                    sx={invoiceFieldSx}
                    error={Boolean(getFieldError("contactPhone"))}
                    helperText={getFieldError("contactPhone") || undefined}
                    onChange={({ target }) => {
                      setContactPhone(formatStaffPhoneInput(target.value));
                      clearFieldError("contactPhone");
                    }}
                    fullWidth
                  />
                </div>
              </div>
            </div>
          </InvoiceSection>

          <InvoiceSection
            label="Details"
            title="Invoice Details"
            variant="plain"
          >
            <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
              <div
                data-invoice-field-error={
                  getFieldError("issueDate") ? "true" : undefined
                }
              >
                <label
                  htmlFor="create-invoice-issue-date"
                  className={`mb-1 block text-sm font-bold ${
                    getFieldError("issueDate")
                      ? "text-red-600"
                      : "text-gray-900"
                  }`}
                >
                  Invoice Date <span className="text-red-500">*</span>
                </label>
                <CalendarDateInput
                  id="create-invoice-issue-date"
                  ariaLabel="Invoice Date"
                  mode="fullDate"
                  iconPosition="left"
                  value={issueDate}
                  error={Boolean(getFieldError("issueDate"))}
                  onChange={(value) => {
                    setIssueDate(value);
                    clearFieldError("issueDate");
                  }}
                />
                {getFieldError("issueDate") ? (
                  <p className="mt-1 text-[12px] text-[#E24C4C]">
                    {getFieldError("issueDate")}
                  </p>
                ) : null}
              </div>
              <div
                data-invoice-field-error={
                  getFieldError("dueDate") ? "true" : undefined
                }
              >
                <label
                  htmlFor="create-invoice-due-date"
                  className={`mb-1 block text-sm font-bold ${
                    getFieldError("dueDate") ? "text-red-600" : "text-gray-900"
                  }`}
                >
                  Due Date <span className="text-red-500">*</span>
                </label>
                <CalendarDateInput
                  id="create-invoice-due-date"
                  ariaLabel="Due Date"
                  mode="fullDate"
                  iconPosition="left"
                  value={dueDate}
                  error={Boolean(getFieldError("dueDate"))}
                  onChange={(value) => {
                    setDueDate(value);
                    setDueDateManuallyEdited(true);
                    clearFieldError("dueDate");
                  }}
                />
                {getFieldError("dueDate") ? (
                  <p className="mt-1 text-[12px] text-[#E24C4C]">
                    {getFieldError("dueDate")}
                  </p>
                ) : null}
              </div>
              <div>
                <label className="mb-1 block text-sm font-bold text-gray-900">
                  Reference / PO Number (Optional)
                </label>
                <TextField
                  placeholder="Enter Reference / PO Number"
                  value={referenceNumber}
                  sx={invoiceFieldSx}
                  onChange={({ target }) => {
                    setReferenceNumber(target.value);
                  }}
                  fullWidth
                />
              </div>
            </div>
          </InvoiceSection>

          <InvoiceSection label="Items" title="Create Invoice" variant="plain">
            {/* Claude Design helper copy (grammar of "code will populate"
              corrected). */}
            <p className="mb-4 text-sm leading-6 text-gray-600">
              To generate an invoice select description options from drop down
              and code will populate. Then enter quantity. account# will
              populate along with unit price. The tax rate will be shown, to not
              charge tax for a line use drop down and select Tax Exempt. Verify
              all totals.
            </p>
            <InvoiceLineItemsTable
              accountNumber={accountNumber}
              allowCustomDescription={activeTab === "custom"}
              catalogOptions={lineItemCatalogOptions}
              defaultTaxRatePercentage={defaultTaxRate}
              lineItems={lineItems}
              lineErrors={lineItemErrors}
              onChange={handleLineItemsChange}
              tableError={getFieldError("lineItems")}
            />

            <div className="mt-6 flex flex-col items-end gap-2">
              <div className="w-full max-w-[320px] space-y-2 text-sm">
                <div className="flex justify-between text-gray-600">
                  <span>Subtotal</span>
                  <span className="font-semibold text-gray-900">
                    {formatCurrency(subtotalInCents)}
                  </span>
                </div>
                <div className="flex justify-between border-b border-gray-200 pb-2 text-gray-600">
                  {/* TICK-10 (B5): "Tax (rate%)" shape per mock. */}
                  <span>{taxSummaryLabel}</span>
                  <span className="font-semibold text-gray-900">
                    {formatCurrency(taxInCents)}
                  </span>
                </div>
                <div className="flex items-end justify-between pt-1">
                  <span className="text-base font-extrabold text-gray-900">
                    TOTAL DUE
                  </span>
                  <span className="text-2xl font-extrabold text-gray-900">
                    {formatCurrency(totalInCents)}
                  </span>
                </div>
              </div>
            </div>

            <div className="mt-6">
              <TextField
                placeholder="Add internal notes that won't appear on the invoice."
                value={notes}
                sx={invoiceFieldSx}
                onChange={({ target }) => {
                  setNotes(target.value);
                }}
                multiline
                minRows={2}
                fullWidth
              />
            </div>
          </InvoiceSection>

          <div className="flex flex-wrap items-center justify-end gap-5">
            {/* Each field shows its own message; this is one summary. */}
            {firstFormError ? (
              <Alert severity="error" className="w-full">
                {FIX_MARKED_FIELDS_MESSAGE}
              </Alert>
            ) : null}
            <PreviewInvoiceButton onClick={handleOpenPreview} />
            <Button
              variant="contained"
              disabled={submitting}
              onClick={() =>
                void submitInvoice({
                  createAsDraft: false,
                  navigateAfterSave: false,
                })
              }
              startIcon={
                submitting ? (
                  <CircularProgress color="inherit" size={16} />
                ) : undefined
              }
              sx={createInvoiceButtonSx}
            >
              Create Invoice
            </Button>
          </div>
        </div>

        <InvoiceCreatedDialog
          actionError={createdInvoiceActionError}
          customerName={actualPayer.customerName}
          message="Invoice has been generated, would you like to print or email the invoice?"
          onClose={() => {
            setCreatedInvoice(null);
            setCreatedInvoicePreview(null);
            setCreatedInvoiceActionError(null);
            navigate(RoutePaths.chamberAdmin_invoicing_manage);
          }}
          emailing={emailingInvoiceDocument}
          onEmail={async (): Promise<InvoiceCreatedEmailResult> => {
            if (!createdInvoice) {
              throw new UserFacingError(
                "We could not find the new invoice to email it. Close this window and email it from Invoices & Payments > Manage all Invoices & Payments.",
              );
            }

            try {
              setCreatedInvoiceActionError(null);
              const result = await runManageInvoiceActionOperation({
                action: "EMAIL_DOCUMENT",
                chamberId,
                execute: (input) =>
                  emailCmpInvoiceDocument({
                    variables: { input },
                  }),
                input: {
                  chamberId,
                  invoiceId: createdInvoice.id,
                  isCmpInvoiceModuleTestData:
                    shouldMarkCmpInvoiceModuleTestData || undefined,
                  mode: CmpInvoiceDocumentEmailModeEnum.InvoiceDocument,
                  operationId: undefined,
                },
                invoiceId: createdInvoice.id,
                isAuthoritativelyCompleted: (mutationResult) =>
                  !!mutationResult.data?.emailCmpInvoiceDocument
                    .actionCompletedSuccessfully &&
                  wasOutboundEmailSent(
                    mutationResult.data.emailCmpInvoiceDocument.outboundEmail,
                  ),
              });
              const payload = result.data?.emailCmpInvoiceDocument;

              if (
                !payload?.actionCompletedSuccessfully ||
                !wasOutboundEmailSent(payload.outboundEmail)
              ) {
                throw createInvoiceEmailFailureError({
                  email: payload?.outboundEmail,
                  label: `the email for invoice ${createdInvoice.invoiceNumber}`,
                });
              }

              const actualRecipientEmail = payload.outboundEmail.toEmail.trim();

              if (!actualRecipientEmail) {
                throw new Error(
                  "The saved email result did not include its recipient.",
                );
              }

              const successContent = getInvoiceEmailSuccessContent({
                customerName: actualPayer.customerName,
                email: payload.outboundEmail,
              });

              return {
                actualRecipientEmail,
                successHighlights: successContent.highlights,
                successMessage: successContent.message,
                successNote: successContent.note,
              };
            } catch (error) {
              // The email may have gone out, so unknown errors ask staff
              // to check the invoice first.
              const text = getUserFacingErrorText(
                error,
                INVOICE_EMAIL_UNCONFIRMED_TEXT,
              );
              setCreatedInvoiceActionError(
                text === NETWORK_ERROR_TEXT ? INVOICE_EMAIL_UNCONFIRMED_TEXT : text,
              );
              throw error;
            }
          }}
          onPrint={async () => {
            if (!createdInvoice) {
              return;
            }

            // Claude Design: Print Invoice opens the saved invoice preview.
            setCreatedInvoiceActionError(null);
            const result = await getCreatedInvoiceDetail({
              variables: {
                input: { chamberId, invoiceId: createdInvoice.id },
              },
            });
            const detail = result.data?.getCmpInvoiceDetail;

            if (!detail || detail.invoice.id !== createdInvoice.id) {
              throw new UserFacingError(
                "We could not open the invoice preview. Open the invoice from Invoices & Payments > Manage all Invoices & Payments.",
              );
            }

            setCreatedInvoicePreview({
              detail,
              row: manageInvoiceRowFromDetail(detail),
            });
          }}
          open={Boolean(createdInvoice) && !createdInvoicePreview}
          printing={loadingCreatedInvoiceDetail}
          recipientEmail={createdInvoice?.recipientEmail}
          title="Invoice Created"
        />

        <InvoicePreviewModal
          actionLoading={downloadingPdf}
          canApplyPayment={false}
          canPrintPdf={Boolean(
            createdInvoicePreview?.detail.availableActions.some(
              (action) =>
                action.action === CmpInvoiceActionTypeEnum.DownloadPdf &&
                action.available,
            ),
          )}
          canSendInvoice={false}
          chamber={chamber}
          detail={createdInvoicePreview?.detail || null}
          errorMessage={createdInvoiceActionError}
          loading={false}
          onAddPayment={() => undefined}
          onClose={() => setCreatedInvoicePreview(null)}
          onPrintPdf={() => void handleDownloadCreatedInvoicePdf()}
          onSendInvoice={() => undefined}
          onSendReceipt={() => undefined}
          open={Boolean(createdInvoicePreview)}
          row={createdInvoicePreview?.row || null}
          sendIsResend={false}
        />
      </FormPageShell>
    </InvoiceDesignScope>
  );
};

export default CreateInvoice;
